/* ==========================================================================
   Herbst-Vorschau: Auszug aus Entwurf 1 „Impuls“ (assets/entwurf1.js, Branch
   entwurf-1-impuls) — Hilfsfunktionen, Bundle-Builder und Produkt-Streifen.
   ========================================================================== */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pad2 = (n) => String(n).padStart(2, '0');

  /* ---------- Geld im Shop-Format (shop.money_format) ---------- */
  const money = (cents, format) => {
    const fmt = format || document.documentElement.dataset.e1MoneyFormat || '€ {{amount_with_comma_separator}}';
    const value = Math.round(Number(cents) || 0);
    const abs = Math.abs(value);
    const euros = Math.floor(abs / 100);
    const rest = pad2(abs % 100);
    const group = (n, sep) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    let out;
    if (fmt.includes('amount_no_decimals_with_comma_separator')) {
      out = fmt.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, group(euros, '.'));
    } else if (fmt.includes('amount_with_comma_separator')) {
      out = fmt.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${group(euros, '.')},${rest}`);
    } else if (fmt.includes('amount_with_apostrophe_separator')) {
      out = fmt.replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, `${group(euros, "'")}.${rest}`);
    } else if (fmt.includes('amount_no_decimals')) {
      out = fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/, group(euros, ','));
    } else {
      out = fmt.replace(/\{\{\s*amount\s*\}\}/, `${group(euros, ',')}.${rest}`);
    }
    out = out.replace(/<[^>]+>/g, '').trim();
    return value < 0 ? `−${out}` : out;
  };

  /* Gramm aus Variantentitel („100g“, „250 g“, „1000g / Ganze Bohne“) */
  const parseGrams = (title) => {
    const m = /(\d+(?:[.,]\d+)?)\s*(?:g|gramm)\b/i.exec(title || '');
    return m ? parseFloat(m[1].replace(',', '.')) : null;
  };

  const getCart = () => document.querySelector('cart-drawer');

  /* Mehrere Artikel auf einmal in den Warenkorb (wie product-form.js, aber JSON) */
  const addItems = async (items, opener) => {
    const cart = getCart();
    const body = { items };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((s) => s.id).join(',');
      body.sections_url = window.location.pathname;
      cart.setActiveElement(opener || document.activeElement);
    }
    const res = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Das hat nicht geklappt.');
    if (window.publish && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'e1', cartData: json });
    }
    if (cart) {
      cart.classList.remove('is-empty');
      cart.renderContents(json);
    } else {
      window.location.href = window.routes.cart_url;
    }
    return json;
  };

  const setLoading = (btn, on) => {
    if (!btn) return;
    btn.classList.toggle('loading', on);
    btn.toggleAttribute('aria-disabled', on);
    const spinner = btn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
  };



  /* ==========================================================================
     Bundle-Builder
     ========================================================================== */
  class E1Bundle extends HTMLElement {
    connectedCallback() {
      this.fmt = this.dataset.moneyFormat;
      this.slotsCount = Math.min(4, Math.max(2, parseInt(this.dataset.slots, 10) || 3));
      this.discounts = { 2: Number(this.dataset.discount2) || 0, 3: Number(this.dataset.discount3) || 0, 4: Number(this.dataset.discount4) || 0 };
      this.pool = [...this.querySelectorAll('[data-pool-item]')].map((el, i) => ({
        el, index: i,
        title: el.dataset.title, image: el.dataset.image, url: el.dataset.url,
        variants: JSON.parse(el.dataset.variants || '[]').filter((v) => v.available),
      }));
      this.slots = Array.from({ length: this.slotsCount }, () => null);
      this.slotEls = [...this.querySelectorAll('[data-slot]')];
      this.sumEl = this.querySelector('[data-sum]');
      this.button = this.querySelector('[data-bundle-submit]');
      this.errorEl = this.querySelector('[data-bundle-error]');

      this.pool.forEach((p) => {
        if (!p.variants.length) { p.el.disabled = true; return; }
        p.el.addEventListener('click', () => this.toggle(p.index));
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-slot-remove]');
        if (rm) this.clearSlot(Number(rm.closest('[data-slot]').dataset.slot));
      });
      this.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-slot-variant]');
        if (!sel) return;
        const slot = this.slots[Number(sel.closest('[data-slot]').dataset.slot)];
        if (slot) { slot.variant = Number(sel.value); this.renderSum(); }
      });
      this.button.addEventListener('click', () => this.submit());

      if (this.dataset.preselect === 'true') {
        this.pool.filter((p) => p.variants.length).slice(0, this.slotsCount).forEach((p) => this.add(p.index));
      }
      this.render();
    }
    toggle(index) {
      const existing = this.slots.findIndex((s) => s && s.pool === index);
      if (existing > -1) return this.clearSlot(existing);
      this.add(index);
    }
    add(index) {
      const free = this.slots.findIndex((s) => s === null);
      const item = this.pool[index];
      if (free < 0 || !item || !item.variants.length) {
        if (free < 0) this.flash(`Alle ${this.slotsCount} Plätze sind voll. Nimm eins raus, um zu tauschen.`);
        return;
      }
      this.slots[free] = { pool: index, variant: item.variants[0].id };
      this.render();
    }
    clearSlot(i) { this.slots[i] = null; this.render(); }
    flash(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.errorEl.hidden = true; }, 3500);
    }
    render() {
      this.slotEls.forEach((el, i) => {
        const s = this.slots[i];
        el.classList.toggle('is-filled', !!s);
        if (!s) {
          el.innerHTML = `<span class="e1-slot__ph">${i + 1}. Platz — tipp auf ein Produkt</span>`;
          return;
        }
        const p = this.pool[s.pool];
        const opts = p.variants.map((v) => `<option value="${v.id}" ${v.id === s.variant ? 'selected' : ''}>${v.title} · ${money(v.price, this.fmt)}</option>`).join('');
        el.innerHTML = `
          <img class="e1-slot__img" src="${p.image}" alt="" width="64" height="64" loading="lazy">
          <div>
            <div class="e1-slot__title">${p.title}</div>
            ${p.variants.length > 1
              ? `<select class="e1-select e1-slot__select" data-slot-variant aria-label="Größe für ${p.title}">${opts}</select>`
              : `<div class="e1-slot__price">${p.variants[0].title !== 'Default Title' ? `${p.variants[0].title} · ` : ''}${money(p.variants[0].price, this.fmt)}</div>`}
          </div>
          <button type="button" class="e1-slot__remove" data-slot-remove aria-label="${p.title} aus der Box nehmen">×</button>`;
      });
      this.pool.forEach((p) => p.el.classList.toggle('is-selected', this.slots.some((s) => s && s.pool === p.index)));
      this.renderSum();
    }
    renderSum() {
      const filled = this.slots.filter(Boolean);
      const single = filled.reduce((sum, s) => {
        const v = this.pool[s.pool].variants.find((x) => x.id === s.variant) || this.pool[s.pool].variants[0];
        return sum + v.price;
      }, 0);
      const pct = filled.length >= 2 ? (this.discounts[Math.min(4, filled.length)] || 0) : 0;
      const box = Math.round(single * (1 - pct / 100));
      const missing = this.slotsCount - filled.length;
      if (!filled.length) {
        this.sumEl.innerHTML = `Noch leer. Drei Packungen rein, <strong>−${this.discounts[3] || this.discounts[2]} %</strong> raus.`;
      } else if (pct > 0) {
        this.sumEl.innerHTML = `<span class="e1-bundle__prices">Einzeln <s>${money(single, this.fmt)}</s> → Box <strong>${money(box, this.fmt)}</strong></span><span class="e1-bundle__save">du sparst ${money(single - box, this.fmt)} (−${pct} %)</span>`;
      } else {
        this.sumEl.innerHTML = `Bisher <strong>${money(single, this.fmt)}</strong> — ab 2 Packungen gibt's Rabatt.`;
      }
      this.button.disabled = filled.length < 2;
      this.button.querySelector('span').textContent = missing > 0 && filled.length < 2
        ? `Noch ${missing} ${missing === 1 ? 'Tee' : 'Tees'} auswählen`
        : filled.length < this.slotsCount
          ? `${filled.length} Packungen in den Warenkorb`
          : 'Alles in den Warenkorb';
    }
    async submit() {
      const items = this.slots.filter(Boolean).map((s) => ({ id: s.variant, quantity: 1 }));
      if (!items.length) return;
      setLoading(this.button, true);
      try {
        await addItems(items, this.button);
      } catch (err) {
        this.flash(err.message);
      } finally {
        setLoading(this.button, false);
      }
    }
  }
  if (!customElements.get('e1-bundle-builder')) customElements.define('e1-bundle-builder', E1Bundle);

  /* ==========================================================================
     Produkt-Streifen: Pfeile scrollen um eine Karte
     ========================================================================== */
  class E1Strip extends HTMLElement {
    connectedCallback() {
      const row = this.querySelector('[data-strip-row]');
      const card = row && row.querySelector('.e1-pcard');
      this.querySelectorAll('[data-strip-prev],[data-strip-next]').forEach((b) => {
        b.addEventListener('click', () => {
          const step = card ? card.getBoundingClientRect().width + 16 : 300;
          row.scrollBy({ left: b.hasAttribute('data-strip-next') ? step : -step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });
      });
    }
  }
  if (!customElements.get('e1-product-strip')) customElements.define('e1-product-strip', E1Strip);
})();
